import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MagnifyingGlassIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { workoutPlannerAPI } from '../../services/api'
import { contentLanguage } from '../../i18n/supported'

// UAT 2026-09-15: substituting deletes the replaced exercise's reported sets
// (decided by Reto). `reportedSetCount` > 0 turns the modal into an explicit
// confirmation of that, so the deletion is never silent.
export function SubstituteExerciseModal({ exercise, onConfirm, onClose, isLoading, locationId, locationName, reportedSetCount = 0 }) {
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedExercise, setSelectedExercise] = useState(null)
  // VH-212: the muscle-group scope is now dismissible. It has caused two
  // reported failures (VH-146 and this one) and both times the fault was
  // invisibility, not existence — a user replacing a CORE exercise with a BACK
  // machine to train his stomach cannot guess that the list is filtered by the
  // MACHINE's classification rather than by his intent. So it stays as the
  // default list, but it is shown and it can be dropped in one tap.
  const [muscleScopeCleared, setMuscleScopeCleared] = useState(false)
  // VH-178 (gym report 2026-09-16): the exercise-type scope was the one narrowing
  // nobody could see. Same treatment as the muscle scope — shown, and dismissible.
  const [typeScopeCleared, setTypeScopeCleared] = useState(false)

  const isAddMode = exercise?.isNewActivity
  const isSearching = searchTerm.trim().length > 0

  // Both scopes narrow the default "similar movements" list, and both are
  // dropped the moment the user actively searches. An explicit search is a
  // statement of intent — the user has named what they want, and a filter they
  // cannot see must not silently withhold it.
  //
  // The muscle-group scope went first: searching "Hip Thrust" (glutes) while
  // replacing a different-group exercise returned nothing.
  //
  // The type scope had the same fault and was missed, because it is invisible
  // on the exercise type almost everything uses. Of 48 active exercises, 40 are
  // `strength` — so substituting one strength exercise for another searched
  // fine, while `tabata`, `running` and `cardio` have exactly ONE member each
  // and `group_class` two. Replacing Tabata scoped the catalog to Tabata, so
  // every search returned nothing at all. Reported from production.
  //
  // Keeping the scope for the default view is still right: without a search
  // term, "exercises like this one" is the useful list. It is only as a
  // constraint on an explicit search that it becomes a trap.
  const scopeType =
    !isAddMode && !isSearching && !typeScopeCleared
      ? exercise?.snapshot_exercise_type
      : undefined
  const scopeMuscle =
    !isAddMode && !isSearching && !muscleScopeCleared
      ? exercise?.snapshot_muscle_group
      : undefined
  // Shown as a chip only while it is actually narrowing the list — on search it
  // is already dropped, and saying otherwise would be a lie about the results.
  const muscleScopeVisible = Boolean(scopeMuscle)
  const typeScopeVisible = Boolean(scopeType)
  const typeScopeLabel = scopeType
    ? t(`exercises.types.${String(scopeType).toLowerCase()}`, { defaultValue: String(scopeType).replace('_', ' ') })
    : ''

  // The scope value is the stored enum ("CORE", "BACK"), not display text.
  // Interpolating it raw produced "Ähnliche Übungen: CORE" in German — an
  // English enum leaking into a translated sentence. `exercises.muscleGroups`
  // is the existing lowercase-keyed map for exactly this. Falls back to the
  // raw value so an enum this map has not caught up with degrades to a
  // readable word rather than a missing-key string.
  const muscleScopeLabel = scopeMuscle
    ? t(`exercises.muscleGroups.${String(scopeMuscle).toLowerCase()}`, {
        defaultValue: scopeMuscle,
      })
    : ''

  // VH-146: an exercise you cannot perform where you are standing is never a
  // useful suggestion, so the location scopes the list by default — unlike the
  // muscle/type scopes above, it is NOT dropped on search, because searching
  // states which exercise you want, not which gym you are in.
  //
  // `location_fallback` asks the server to widen rather than return nothing:
  // availability links are sparse on real data (VH-139 found
  // link_primary_and_twins only fires on create/update), and an empty picker
  // mid-session reads as broken. When it widens, the banner below says so —
  // silently offering a machine at the other gym would be worse than empty.
  const { data: exercisesData, isLoading: exercisesLoading } = useQuery({
    queryKey: ['exercises-substitute', searchTerm, scopeType, scopeMuscle, locationId],
    queryFn: () => workoutPlannerAPI.getExercises({
      search_term: searchTerm || undefined,
      exercise_type: scopeType || undefined,
      muscle_group: scopeMuscle || undefined,
      location_id: locationId || undefined,
      location_fallback: locationId ? true : undefined,
      limit: 50
    }),
    keepPreviousData: true
  })

  const exercises = exercisesData?.data?.exercises || []

  const equipmentLabel = (ex) => {
    if (ex.equipment) {
      return currentLanguage === 'de' && ex.equipment.name_de
        ? ex.equipment.name_de
        : ex.equipment.name_en
    }
    return null
  }

  // VH-196: the default reason for substituting is "Equipment busy", so an
  // exercise on the SAME station is the one option that cannot help. Say so
  // rather than hide it — it may still be wanted when the reason is variety,
  // and silently filtering it would look like a missing exercise.
  const replacedEquipmentId = exercise?.source_equipment_id ?? null
  const replacedEquipmentName = exercise?.snapshot_equipment_name_en ?? null
  const isSameEquipment = (ex) => {
    if (isAddMode) return false
    const id = ex.equipment?.id ?? ex.equipment_id ?? null
    if (replacedEquipmentId != null && id != null) return id === replacedEquipmentId
    if (replacedEquipmentName && ex.equipment?.name_en) {
      return ex.equipment.name_en === replacedEquipmentName
    }
    return false
  }
  // A DELETED location keeps its id and loses its name (the availability rows
  // still point at it, so the filter is real and the results are correct — only
  // the label is gone). Rendering the banner with an empty name would read as
  // "no location", which is the opposite of what is happening, so name it
  // generically rather than blankly.
  const locationLabel = locationName || t('programs.unnamedLocation')

  // VH-178 (gym report 2026-09-16): nothing is hidden any more. The server lists
  // the exercises available at this gym first and the rest of the catalogue after
  // them; here they are split into two labelled groups, so an exercise that exists
  // elsewhere is visible as such instead of silently missing.
  const availableHere = (ex) =>
    !locationId
    || ex.location_independent
    || (ex.available_locations || []).some((loc) => loc.id === locationId)
  const hereExercises = locationId ? exercises.filter(availableHere) : exercises
  const elsewhereExercises = locationId ? exercises.filter((ex) => !availableHere(ex)) : []

  const handleConfirm = () => {
    if (!selectedExercise) return
    onConfirm(selectedExercise)
  }

  const renderExerciseRow = (ex) => (
    <div
      key={ex.id}
      onClick={() => setSelectedExercise(ex)}
      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
        selectedExercise?.id === ex.id
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="font-medium text-gray-900">
            {currentLanguage === 'de' && ex.name_de ? ex.name_de : ex.name_en}
          </p>
          <p className="text-sm text-gray-500">
            {ex.exercise_type && (
              <span className="capitalize">{ex.exercise_type.replace('_', ' ')}</span>
            )}
            {ex.muscle_group && <> • {ex.muscle_group}</>}
          </p>
          {/* VH-196: name the MACHINE. Two exercises on one
              station ("Shoulder Press Machine Training" and
              "Lateral Raise Machine for Shoulders" are both
              equipment 10) read as two machines when only
              type and muscle group are shown. */}
          {equipmentLabel(ex) && (
            <p className="text-sm text-gray-500">{equipmentLabel(ex)}</p>
          )}
          {isSameEquipment(ex) && (
            <p className="text-xs font-medium text-amber-700 mt-0.5">
              {t('workouts.sameMachineWarning')}
            </p>
          )}
        </div>
        {selectedExercise?.id === ex.id && (
          <CheckCircleIcon className="h-5 w-5 text-primary-600" />
        )}
      </div>
    </div>
  )

  // VH-178: one renderer, two groups — the exercises available here, then the rest
  // under a heading that says why they are separate.
  const renderGroup = (rows, heading) => (
    <div className="space-y-2" data-testid={heading ? 'substitute-group-elsewhere' : 'substitute-group-here'}>
      {heading && (
        <p className="pt-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{heading}</p>
      )}
      {rows.map((ex) => renderExerciseRow(ex))}
    </div>
  )

  return (
    // The panel is a flex COLUMN whose middle section is the only thing that
    // scrolls. It used to be three plain flow children inside
    // `max-h-[80vh] overflow-hidden`, with the results list capped at a fixed
    // 400px. Header + banners + 400px list + footer comes to roughly 610px of
    // intrinsic height, so on a phone (80vh ≈ 530-590px) the footer was clipped
    // outside a box that could not scroll: you could pick an exercise but the
    // Confirm button was neither visible nor tappable. Desktop viewports were
    // tall enough to hide the fault entirely.
    //
    // `flex-1 min-h-0` on the list is what makes this hold at ANY height — the
    // min-h-0 is required, or the flex child refuses to shrink below its
    // content and the clipping comes straight back.
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isAddMode ? t('programs.addExercise') : t('programs.substituteExercise')}
              </h3>
              {!isAddMode && (
                <p className="text-sm text-gray-500">
                  {t('programs.substitutingFrom')}: <span className="font-medium">{exercise.snapshot_exercise_name_en}</span>
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {!isAddMode && reportedSetCount > 0 && (
          <div
            role="alert"
            data-testid="substitute-deletes-reported"
            className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 flex-shrink-0"
          >
            {t('programs.substituteDeletesReported', {
              count: reportedSetCount,
              name: exercise.snapshot_exercise_name_en,
            })}
          </div>
        )}

        <div className="px-6 py-4 flex-1 min-h-0 flex flex-col">
          <div className="relative mb-4 flex-shrink-0">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('exercises.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          {muscleScopeVisible && (
            <div className="mb-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setMuscleScopeCleared(true)}
                className="inline-flex items-center rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-xs text-primary-800 hover:bg-primary-100"
                data-testid="substitute-muscle-scope"
              >
                {t('programs.muscleScopeChip', { muscle: muscleScopeLabel })}
                <span className="ml-2 font-medium underline">
                  {t('programs.clearMuscleScope')}
                </span>
                <XMarkIcon className="ml-1 h-3.5 w-3.5" />
              </button>
            </div>
          )}


          {typeScopeVisible && (
            <div className="mb-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setTypeScopeCleared(true)}
                className="inline-flex items-center rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-xs text-primary-800 hover:bg-primary-100"
                data-testid="substitute-type-scope"
              >
                {t('programs.typeScopeChip', { type: typeScopeLabel })}
                <span className="ml-2 font-medium underline">
                  {t('programs.clearTypeScope')}
                </span>
                <XMarkIcon className="ml-1 h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {!locationId && (
            <p
              className="mb-3 flex-shrink-0 text-xs text-gray-500"
              data-testid="substitute-location-none"
            >
              {t('programs.noLocationScope')}
            </p>
          )}

          {locationId && (
            <p className="mb-3 flex-shrink-0 text-xs text-gray-500" data-testid="substitute-location-scope">
              {t('programs.availableAtLocation', {
                location: locationLabel,
                defaultValue: 'Showing exercises available at {{location}}',
              })}
            </p>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
            {exercisesLoading ? (
              <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
            ) : exercises.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{t('exercises.noResults')}</div>
            ) : (
              <>
                {renderGroup(hereExercises, null)}
                {elsewhereExercises.length > 0 && renderGroup(
                  elsewhereExercises,
                  t('programs.notAvailableAtLocation', {
                    location: locationLabel,
                    defaultValue: 'Not available at {{location}}',
                  })
                )}
              </>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <button onClick={onClose} className="btn-outline" data-testid="substitute-cancel">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedExercise || isLoading}
            className="btn-primary"
            data-testid="substitute-confirm"
          >
            {isLoading
              ? t('common.saving')
              : !isAddMode && reportedSetCount > 0
                ? t('programs.confirmSubstituteDelete')
                : t('programs.confirmSubstitute')}
          </button>
        </div>
      </div>
    </div>
  )
}
